/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Breadcrumbs, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Link, matchPath, useLocation } from 'react-router-dom';
import { useLinkStyles } from 'components/common/useLinkStyles';
import { useUserAccounts } from '../../redux/features/user/userAccounts/useUserAccounts';
import Submission from '../../components/submission/Submission';
import useSubmission from '../../components/submission/useSubmission';
import DraftFooter from '../../components/submission/DraftFooter';
import DraftDrawer from '../../components/submission/DraftDrawer';
import { isStatusReadOnly } from '../../models/SubmissionStatus';
import { RoutePath } from '../../shared/enums/RoutePath';

const useStyles = makeStyles(theme => ({
  alert: {
    backgroundColor: theme.palette.error.main,
  },
}));

function DraftDetail({ readOnly = false }) {
  document.title = 'Draft Submission - Patent E-filing';
  const classes = useStyles();
  const linkClass = useLinkStyles();
  const location = useLocation();

  const { user, currentOrganization } = useUserAccounts();

  const { t } = useTranslation();
  const {
    procedure,
    submission,
    setSubmission,
    submissionId,
    validationSchema,
    uiSchema,
    setUiSchema,
    workflowValidations,
    setWorkflowValidations,
    dispatchDraftState,
    errors,
    setErrors,
    extraErrors,
    setExtraErrors,
    onSave,
    onSaveAndClose,
    onClose,
    openDialog,
    closeDialog,
    confirmDialog,
    isDialogOpen,
    isDrawerOpen,
    toggleDrawer,
    initialSubmissionProcedure,
    isVersionCorrect,
  } = useSubmission();

  const isPaymentPage = () => !!matchPath(location?.pathname, RoutePath.PROCEDURE_PAYMENT.path);

  // We need to compute the readonly property in order to avoid to make a submission editable when the user access the edit link directly
  // In that case the readonly prop it is not passed as expected so we need to validate against the submission status
  const computedReadOnly = readOnly || isStatusReadOnly(submission?.status) || !isVersionCorrect;

  const breadcrumbTo = RoutePath.generateRoutePath(RoutePath.PROCEDURE_SEARCH, {
    procedure: procedure.id,
    initialSubmissionProcedure,
  });

  return (
    <>
      <div className="submissionContainer">
        <Breadcrumbs aria-label="breadcrumb" className={classes.breadcrumbs}>
          <Link className={linkClass.link} to={breadcrumbTo}>
            {t(procedure.label)}
          </Link>
          <Typography color="textPrimary">{t('submission.draft')}</Typography>
        </Breadcrumbs>
        {!isVersionCorrect && (
          <Typography variant="h2" className={classes.alert}>
            <strong>{t('submission.incompatible.version')}</strong>
          </Typography>
        )}
        <Submission
          readOnly={computedReadOnly}
          uiSchema={uiSchema}
          setUiSchema={setUiSchema}
          validationSchema={validationSchema}
          submission={submission}
          setSubmission={setSubmission}
          submissionId={submissionId}
          dispatchDraftState={dispatchDraftState}
          setWorkflowValidations={setWorkflowValidations}
          setErrors={setErrors}
          extraErrors={extraErrors}
          setExtraErrors={setExtraErrors}
        />
      </div>
      <DraftFooter
        readOnly={computedReadOnly}
        submission={submission}
        submissionId={submissionId}
        errors={errors}
        isValid={workflowValidations.isValid}
        uiSchema={uiSchema}
        onSave={onSave}
        isPaymentPage={isPaymentPage}
        onSaveAndClose={onSaveAndClose}
        onClose={onClose}
        onSendToPackage={openDialog}
        closeDialog={closeDialog}
        confirmDialog={confirmDialog}
        isDialogOpen={isDialogOpen}
        isDrawerOpen={isDrawerOpen}
        toggleDrawer={toggleDrawer}
      />
      <DraftDrawer
        user={user}
        currentOrganization={currentOrganization}
        submission={submission}
        readOnly={computedReadOnly}
        uiSchema={uiSchema}
        sectionValidations={workflowValidations?.sectionValidations}
        isDrawerOpen={isDrawerOpen}
        toggleDrawer={toggleDrawer}
      />
    </>
  );
}

export default DraftDetail;
